import { motion } from 'framer-motion';
import { PhoneIcon, EnvelopeIcon, MapPinIcon, HomeIcon, UserGroupIcon, SparklesIcon } from '@heroicons/react/24/outline';

const About = () => {
  const services = [
    { 
      category: 'Konut Satış', 
      items: ['Villa', 'Daire', 'Dublex', 'Penthouse', 'Stüdyo', 'Müstakil Ev'] 
    },
    { 
      category: 'Ticari Gayrimenkul', 
      items: ['Ofis', 'Dükkan', 'Plaza', 'İşyeri', 'Depo', 'Arsa'] 
    },
    { 
      category: 'Danışmanlık Hizmetleri', 
      items: ['Değerleme', 'Yatırım Danışmanlığı', 'Hukuki Destek', 'Kredi Danışmanlığı', 'Tapu İşlemleri', 'Sigorta'] 
    },
  ];

  const stats = [
    {
      number: '500+',
      label: 'Mutlu Müşteri',
      icon: UserGroupIcon
    },
    {
      number: '1000+',
      label: 'Satılan Emlak',
      icon: HomeIcon
    },
    {
      number: '15+',
      label: 'Yıllık Deneyim',
      icon: SparklesIcon
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 
              className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="text-primary-600 dark:text-primary-400">Hakkımızda</span>
            </motion.h1>
            <motion.p 
              className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Türkiye'nin önde gelen emlak danışmanlık şirketlerinden biri olarak, hayalinizdeki evi bulmanıza yardımcı oluyoruz.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex flex-col sm:flex-row justify-center gap-4"
            >
              <a
                href="tel:+905551234567"
                className="btn btn-primary inline-flex items-center justify-center"
              >
                Bizi Arayın
                <PhoneIcon className="ml-2 h-5 w-5" />
              </a>
              <a
                href="/contact"
                className="btn btn-secondary inline-flex items-center justify-center"
              >
                İletişime Geçin
                <EnvelopeIcon className="ml-2 h-5 w-5" />
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full mb-4">
                  <stat.icon className="h-8 w-8 text-primary-600 dark:text-primary-400" />
                </div>
                <div className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 dark:text-gray-400">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Us */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <motion.div 
              className="lg:w-1/2"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="relative">
                <div className="w-full h-96 bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900 dark:to-primary-800 rounded-2xl overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80" 
                    alt="Emlak Ofisi"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-secondary-100 dark:bg-secondary-900 rounded-full -z-10"></div>
                <div className="absolute -top-6 -left-6 w-20 h-20 bg-secondary-100 dark:bg-secondary-900 rounded-full -z-10"></div>
              </div>
            </motion.div>
            
            <motion.div 
              className="lg:w-1/2"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                Biz Kimiz?
              </h2>
              <div className="space-y-4 text-gray-600 dark:text-gray-300">
                <p>
                  15 yılı aşkın deneyimimizle, Türkiye'nin dört bir yanında binlerce müşterimize hayallerindeki evi bulmalarında yardımcı olduk.
                </p>
                <p>
                  Uzman kadromuz ve yapay zeka destekli sesli asistanımız ile 7/24 hizmetinizdeyiz. Modern teknoloji ve geleneksel danışmanlık hizmetlerini bir araya getirerek, emlak arama sürecinizi kolaylaştırıyoruz.
                </p>
                <p>
                  Müşteri memnuniyeti bizim için her şeyden önemlidir. Şeffaf, güvenilir ve profesyonel hizmet anlayışımızla sektörde fark yaratıyoruz.
                </p>
                <div className="pt-4 flex items-center text-gray-700 dark:text-gray-300">
                  <MapPinIcon className="h-5 w-5 mr-2 text-primary-600 dark:text-primary-400" />
                  <span>İstanbul, Türkiye</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="section-title">Hizmetlerimiz</h2>
            <p className="section-subtitle">
              Size sunduğumuz profesyonel emlak danışmanlık hizmetleri
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="card"
              >
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  {service.category}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {service.items.map((item, i) => (
                    <span 
                      key={i}
                      className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-sm font-medium rounded-full"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary-600 to-secondary-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <PhoneIcon className="h-16 w-16 mx-auto mb-6 opacity-90" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Sesli Asistanımızla Tanışın
            </h2>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto mb-8">
              Yapay zeka destekli sesli asistanımız ile emlak ihtiyaçlarınız hakkında konuşun. 
              Sağ alttaki butona tıklayarak hemen başlayın!
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
